import { Input, Space, Popover } from 'antd';
import NavigationButton from './NavigationButton';
import { ActiveHomeIcon, ActiveBrowseIcon, BrowseIcon, HomeIcon, SearchIcon } from '../../../Icons';
import { FiMoreHorizontal } from 'react-icons/fi';
import { TrackActionsWrapper } from '../../../Actions/TrackActions';
import { AddSongToLibraryButton } from '../../../Actions/AddSongToLibrary';

// Utils
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { searchHistoryActions } from '../../../../store/slices/searchHistory';
import { getItemDescription } from '../../../../utils/getDescription';
import { PLAYLIST_DEFAULT_IMAGE } from '../../../../constants/spotify';
import { playerService } from '../../../../services/player';
import { mapJioSaavnSongToSpotifyTrack } from '../../../../utils/jiosaavn-adapters';
import axios from '../../../../axios';
import type { Track } from '../../../../interfaces/track';

const getSearchQueryFromPath = (pathname: string): string => {
  if (!pathname.startsWith('/search/')) return '';
  const sub = pathname.replace(/^\/search\//, '');
  const segments = sub.split('/');
  const querySegment = segments[0] || '';
  try {
    return decodeURIComponent(querySegment);
  } catch {
    return querySegment;
  }
};

const RecentSearchesDropdown = memo(({ close, width }: { close: () => void; width: number }) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation(['search']);
  const navigate = useNavigate();
  const items = useAppSelector((state) => state.searchHistory.items);

  if (!items || items.length === 0) {
    return (
      <div
        className="search-dropdown-container"
        style={{ width, padding: '16px 20px' }}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700 }}>
          {t('Recent searches', { ns: 'search' })}
        </h3>
        <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#fff', fontSize: 14 }}>
          Play what you love
        </p>
        <p style={{ margin: 0, color: '#b3b3b3', fontSize: 13 }}>
          Search for artists, songs, podcasts, and more.
        </p>
      </div>
    );
  }

  return (
    <div
      className="search-dropdown-container"
      style={{ width, padding: '10px 0' }}
    >
      <style>{`
        .recent-search-dropdown-item .recent-search-remove-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }
        .recent-search-dropdown-item:hover .recent-search-remove-btn {
          opacity: 1;
        }
        .recent-searches-scrollable::-webkit-scrollbar {
          width: 12px;
        }
        .recent-searches-scrollable::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.3);
          border-radius: 6px;
          border: 3px solid #242424;
        }
        .recent-searches-scrollable:hover::-webkit-scrollbar-thumb {
          background-color: rgba(255, 255, 255, 0.5);
        }
        .recent-searches-scrollable::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
      <div style={{ padding: '0 16px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{t('Recent searches', { ns: 'search' })}</h3>
      </div>
      <div className="recent-searches-scrollable" style={{ maxHeight: 350, overflowY: 'auto', paddingBottom: 8 }}>
        {items.map((item) => {
          let image = item.type === 'playlist' ? PLAYLIST_DEFAULT_IMAGE : '';
          if ((item as any).images && (item as any).images[0]) {
            image = (item as any).images[0].url;
          } else if ((item as any).album && (item as any).album.images && (item as any).album.images[0]) {
            image = (item as any).album.images[0].url;
          }

          const desc = getItemDescription(item);
          const typeLabel = item.type === 'track' ? 'Song' : item.type.charAt(0).toUpperCase() + item.type.slice(1);

          return (
            <div
              key={item.uri}
              onClick={() => {
                close();
                if (item.type === 'track') {
                  dispatch(searchHistoryActions.setItem(item));
                  playerService.startPlayback({ song: item as any, uris: [item.uri], tracks: [item as any] });
                } else if (item.type === 'album') {
                  navigate(`/album/${item.id}`);
                } else if (item.type === 'artist') {
                  navigate(`/artist/${item.id}`);
                } else if (item.type === 'playlist') {
                  navigate(`/playlist/${item.id}`);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                cursor: 'pointer',
              }}
              className="recent-search-dropdown-item"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <img
                src={image}
                alt={item.name}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: item.type === 'artist' ? '50%' : 4,
                  objectFit: 'cover',
                  marginRight: 12,
                }}
              />
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.name}
                </div>
                <div style={{ fontSize: 14, color: '#b3b3b3', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {typeLabel} {desc ? '• ' + desc : ''}
                </div>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(searchHistoryActions.removeItem(item));
                }}
                className="recent-search-remove-btn"
                style={{ padding: 8, cursor: 'pointer', color: '#b3b3b3', display: 'flex', alignItems: 'center' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#b3b3b3')}
              >
                <svg data-encore-id="icon" role="img" aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                  <path d="M2.47 2.47a.75.75 0 0 1 1.06 0L8 6.94l4.47-4.47a.75.75 0 1 1 1.06 1.06L9.06 8l4.47 4.47a.75.75 0 1 1-1.06 1.06L8 9.06l-4.47 4.47a.75.75 0 0 1-1.06-1.06L6.94 8 2.47 3.53a.75.75 0 0 1 0-1.06Z"></path>
                </svg>
              </div>
            </div>
          );
        })}
        <div style={{ padding: '16px 16px 8px 16px' }}>
          <button
            onClick={() => {
              dispatch(searchHistoryActions.clearItems());
            }}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #727272',
              borderRadius: '500px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 700,
              padding: '7px 15px',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#fff';
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#727272';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {t('Clear recent searches', { ns: 'search' })}
          </button>
        </div>
      </div>
    </div>
  );
});

interface LiveSearchDropdownProps {
  query: string;
  suggestions: string[];
  songs: Track[];
  isLoading: boolean;
  selectedIndex: number;
  onSelectSuggestion: (suggestion: string) => void;
  onPlaySong: (song: Track, e?: React.MouseEvent) => void;
  width: number;
}

const renderSuggestionText = (text: string, q: string) => {
  const lowerText = text.toLowerCase();
  const lowerQ = q.toLowerCase();

  // 1. If text starts with q
  if (lowerText.startsWith(lowerQ)) {
    const match = text.slice(0, q.length);
    const rest = text.slice(q.length);
    return (
      <span>
        <strong>{match}</strong>
        {rest && <span>{rest}</span>}
      </span>
    );
  }

  // 2. Check for common prefix (e.g. "lose" in "lose yourself" when q is "loser")
  let commonLen = 0;
  while (
    commonLen < lowerText.length &&
    commonLen < lowerQ.length &&
    lowerText[commonLen] === lowerQ[commonLen]
  ) {
    commonLen++;
  }
  if (commonLen >= 3) {
    const match = text.slice(0, commonLen);
    const rest = text.slice(commonLen);
    return (
      <span>
        <strong>{match}</strong>
        {rest && <span>{rest}</span>}
      </span>
    );
  }

  // 3. Substring match
  const idx = lowerText.indexOf(lowerQ);
  if (idx !== -1) {
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <span>
        {before && <span>{before}</span>}
        <strong>{match}</strong>
        {after && <span>{after}</span>}
      </span>
    );
  }

  return <strong>{text}</strong>;
};

const LiveSearchDropdown = memo(
  ({
    query,
    suggestions,
    songs,
    isLoading,
    selectedIndex,
    onSelectSuggestion,
    onPlaySong,
    width,
  }: LiveSearchDropdownProps) => {
    const likedSongIds = useAppSelector((state) => {
      const set = new Set<string>();
      if (state.likedSongs?.items) {
        for (const item of state.likedSongs.items) {
          if (item?.track?.id) set.add(item.track.id);
        }
      }
      return set;
    });

    const displaySongs = useMemo(() => {
      const seen = new Set<string>();
      const deduped: Track[] = [];
      for (const s of songs) {
        const artist = s.artists?.[0]?.name?.toLowerCase() || '';
        const name = (s.name || '').toLowerCase();
        const key = `${name}-${artist}`;
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(s);
          if (deduped.length >= 2) break;
        }
      }
      return deduped;
    }, [songs]);

    return (
      <div
        className="search-dropdown-container"
        style={{ width, paddingTop: 4, paddingBottom: 6 }}
      >
        {/* Top Shortcuts Bar */}
        <div className="search-dropdown-header">
          <div className="search-shortcut-group">
            <span className="search-key-badge">↑</span>
            <span className="search-key-badge">↓</span>
            <span className="search-shortcut-text">Navigate</span>
          </div>
          <div className="search-shortcut-group">
            <span className="search-key-badge enter-badge">Enter</span>
            <span className="search-shortcut-text">Search</span>
          </div>
        </div>

        {/* Scrollable container with Spotify scrollbar */}
        <div className="live-search-scrollable">
          {/* Query Suggestions List */}
          {suggestions.length > 0 && (
            <div className="search-suggestions-list">
              {suggestions.map((sug, idx) => {
                const isSelected = selectedIndex === idx;
                return (
                  <div
                    key={`sug-${sug}-${idx}`}
                    className={`search-suggestion-item ${isSelected ? 'search-item-selected' : ''}`}
                    onClick={() => onSelectSuggestion(sug)}
                  >
                    <div className="search-suggestion-icon-container">
                      <svg
                        role="img"
                        height="22"
                        width="22"
                        aria-hidden="true"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M10.533 1.27893C5.35215 1.27893 1.12598 5.41887 1.12598 10.5579C1.12598 15.697 5.35215 19.8369 10.533 19.8369C12.767 19.8369 14.8235 19.0671 16.4402 17.7794L20.7929 22.132C21.1834 22.5226 21.8166 22.5226 22.2071 22.132C22.5976 21.7415 22.5976 21.1083 22.2071 20.7178L17.8634 16.3741C19.1616 14.7849 19.94 12.7634 19.94 10.5579C19.94 5.41887 15.7138 1.27893 10.533 1.27893ZM3.12598 10.5579C3.12598 6.55226 6.42768 3.27893 10.533 3.27893C14.6383 3.27893 17.94 6.55226 17.94 10.5579C17.94 14.5636 14.6383 17.8369 10.533 17.8369C6.42768 17.8369 3.12598 14.5636 3.12598 10.5579Z"></path>
                      </svg>
                    </div>
                    <div className="search-suggestion-text">
                      {renderSuggestionText(sug, query)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Songs List */}
          <div className="search-songs-scrollable">
            {isLoading && displaySongs.length === 0 ? (
              <div style={{ padding: '12px 10px', color: '#b3b3b3', fontSize: 13 }}>
                Searching...
              </div>
            ) : displaySongs.length === 0 && !isLoading ? (
              <div style={{ padding: '12px 10px', color: '#b3b3b3', fontSize: 13 }}>
                No results found for "{query}"
              </div>
            ) : (
              displaySongs.map((song, songIdx) => {
                const globalIdx = suggestions.length + songIdx;
                const isSelected = selectedIndex === globalIdx;
                const coverUrl =
                  song.album?.images?.[0]?.url ||
                  song.album?.images?.[1]?.url ||
                  '';
                const artistNames = song.artists?.map((a) => a.name).join(', ') || 'Unknown Artist';
                const isSaved = likedSongIds.has(song.id);

                return (
                  <div
                    key={song.id || `song-${songIdx}`}
                    className={`search-song-row ${isSelected ? 'search-item-selected' : ''}`}
                    onClick={(e) => onPlaySong(song, e)}
                  >
                    {/* Album Cover & Hover Play Overlay */}
                    <div className="search-song-cover-container">
                      {coverUrl ? (
                        <img src={coverUrl} alt={song.name} className="search-song-cover" />
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#333',
                          }}
                        />
                      )}
                      <div className="search-song-play-overlay">
                        <svg
                          role="img"
                          height="18"
                          width="18"
                          aria-hidden="true"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="m7.05 3.606 13.49 7.788a.75.75 0 0 1 0 1.212L7.05 20.394A.75.75 0 0 1 6 19.788V4.212a.75.75 0 0 1 1.05-.606z"></path>
                        </svg>
                      </div>
                    </div>

                    {/* Title & Subtitle */}
                    <div className="search-song-details">
                      <div
                        className="search-song-title"
                        title={song.name}
                      >
                        {song.name}
                      </div>
                      <div className="search-song-subtitle-row" title={`Song • ${artistNames}`}>
                        {song.explicit && <span className="search-explicit-badge">E</span>}
                        <span className="search-song-artist">Song • {artistNames}</span>
                      </div>
                    </div>

                    {/* Actions (Add to Library & More Options) */}
                    <div
                      className="search-song-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="search-song-more-btn">
                        <TrackActionsWrapper track={song} trigger={['click']}>
                          <button
                            type="button"
                            className="search-song-circle-btn"
                            aria-label="More options"
                          >
                            <FiMoreHorizontal size={18} />
                          </button>
                        </TrackActionsWrapper>
                      </div>
                      <AddSongToLibraryButton
                        id={song.id}
                        size={22}
                        isSaved={isSaved}
                        onToggle={() => {}}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }
);

export const Search = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['navbar']);

  const currentQueryFromPath = useMemo(() => getSearchQueryFromPath(location.pathname), [location.pathname]);
  const [inputValue, setInputValue] = useState<string>(currentQueryFromPath);
  const [debouncedQuery] = useDebounce(inputValue.trim(), 250);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(464);
  const isFocusedRef = useRef(false);
  const inputRef = useRef<any>(null);

  // Redux state for current playing song
  const currentTrack = useAppSelector((state) => state.spotify.state?.track_window?.current_track);
  const paused = useAppSelector((state) => state.spotify.state?.paused);
  const isPlaying = !paused;

  // Live search state
  const [liveSongs, setLiveSongs] = useState<Track[]>([]);
  const [globalSuggestions, setGlobalSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  // Sync width on popover open or window resize
  useEffect(() => {
    const updateWidth = () => {
      const el = document.querySelector('.search-input') as HTMLElement;
      if (el) {
        setDropdownWidth(el.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [popoverOpen]);

  // Sync input value if URL param changes externally (e.g. browser back/forward or explicit navigation)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(currentQueryFromPath);
    }
  }, [currentQueryFromPath]);

  // Live search fetch on debounced query (in-place only, no navigation!)
  useEffect(() => {
    if (!debouncedQuery) {
      setLiveSongs([]);
      setGlobalSuggestions([]);
      setIsLoading(false);
      setSelectedIndex(-1);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    const fetchSongs = axios
      .get<any>('/search/songs', { params: { query: debouncedQuery, limit: 10 } })
      .then((res) => res.data?.data?.results || [])
      .catch(() => []);

    const fetchAll = axios
      .get<any>('/search', { params: { query: debouncedQuery } })
      .then((res) => res.data?.data || null)
      .catch(() => null);

    Promise.all([fetchSongs, fetchAll]).then(([songsData, allData]) => {
      if (!isMounted) return;
      const mapped = songsData.map(mapJioSaavnSongToSpotifyTrack);
      setLiveSongs(mapped);

      // Known / curated Spotify completions for popular queries
      const knownCompletions: Record<string, string[]> = {
        loser: ['loser', 'lose yourself', 'losercore', 'loser songs'],
      };

      const lowerQ = debouncedQuery.toLowerCase();
      if (knownCompletions[lowerQ]) {
        setGlobalSuggestions(knownCompletions[lowerQ]);
      } else {
        const suggestions: string[] = [debouncedQuery];
        const seen = new Set<string>([lowerQ]);

        if (allData?.topQuery?.results) {
          for (const t of allData.topQuery.results) {
            const rawTitle = t.title || '';
            const cleanTitle = rawTitle
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, '&')
              .replace(/&#039;/g, "'")
              .replace(/\s*\([^)]*\)/g, '')
              .replace(/\s*\[[^\]]*\]/g, '')
              .trim();
            if (cleanTitle && !seen.has(cleanTitle.toLowerCase()) && cleanTitle.toLowerCase().startsWith(lowerQ)) {
              seen.add(cleanTitle.toLowerCase());
              suggestions.push(cleanTitle.toLowerCase());
              if (suggestions.length >= 4) break;
            }
          }
        }

        if (suggestions.length < 4 && mapped.length > 0) {
          for (const s of mapped) {
            const rawTitle = s.name || '';
            const cleanTitle = rawTitle
              .replace(/\s*\([^)]*\)/g, '')
              .replace(/\s*\[[^\]]*\]/g, '')
              .trim();
            if (cleanTitle && !seen.has(cleanTitle.toLowerCase()) && cleanTitle.toLowerCase().startsWith(lowerQ)) {
              seen.add(cleanTitle.toLowerCase());
              suggestions.push(cleanTitle.toLowerCase());
              if (suggestions.length >= 4) break;
            }
          }
        }

        // Natural music completions (NEVER artist names!)
        const musicCompletions = [
          `${lowerQ} songs`,
          `${lowerQ}core`,
          `${lowerQ} lyrics`,
          `${lowerQ} mix`,
          `${lowerQ} playlist`,
        ];
        for (const mc of musicCompletions) {
          if (suggestions.length >= 4) break;
          if (!seen.has(mc.toLowerCase())) {
            seen.add(mc.toLowerCase());
            suggestions.push(mc);
          }
        }

        setGlobalSuggestions(suggestions.slice(0, 4));
      }
      setIsLoading(false);
      setSelectedIndex(-1);
    });

    return () => {
      isMounted = false;
    };
  }, [debouncedQuery]);

  // Scroll active item into view during keyboard navigation
  useEffect(() => {
    if (selectedIndex >= 0) {
      const el = document.querySelector('.search-item-selected');
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const handleSearchSubmit = (query?: string) => {
    const q = (query !== undefined ? query : inputValue).trim();
    setPopoverOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
    if (q) {
      navigate(`/search/${encodeURIComponent(q)}`);
    } else {
      navigate('/search');
    }
  };

  const onPlaySongInDropdown = (track: Track, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const isCurrentSong = currentTrack?.id === track.id || currentTrack?.uri === track.uri;
    const isCurrentPlaying = isCurrentSong && isPlaying;

    if (isCurrentPlaying) {
      playerService.pausePlayback();
    } else if (isCurrentSong) {
      playerService.startPlayback();
    } else {
      playerService.startPlayback({
        song: track,
        tracks: [track], // ONLY [track]! Never liveSongs!
        uris: [track.uri],
      });
      dispatch(searchHistoryActions.setItem(track));
    }
  };

  const isHome = useMemo(() => location.pathname === '/', [location.pathname]);
  const isBrowse = useMemo(() => {
    return (
      (location.pathname === '/search' ||
        location.pathname === '/search/' ||
        location.pathname.startsWith('/genre/') ||
        location.pathname.startsWith('/browse')) &&
      !inputValue.trim()
    );
  }, [location.pathname, inputValue]);

  return (
    <Space size={8} align='center'>
      <NavigationButton
        text={t('Home')}
        icon={isHome ? <ActiveHomeIcon /> : <HomeIcon />}
        onClick={() => navigate('/')}
      />

      <Popover
        overlayClassName="search-dropdown-popover"
        content={
          !inputValue.trim() ? (
            <RecentSearchesDropdown
              close={() => setPopoverOpen(false)}
              width={dropdownWidth}
            />
          ) : (
            <LiveSearchDropdown
              query={inputValue.trim()}
              suggestions={globalSuggestions}
              songs={liveSongs}
              isLoading={isLoading}
              selectedIndex={selectedIndex}
              onSelectSuggestion={(sug) => handleSearchSubmit(sug)}
              onPlaySong={onPlaySongInDropdown}
              width={dropdownWidth}
            />
          )
        }
        trigger="click"
        open={popoverOpen}
        onOpenChange={(open) => setPopoverOpen(open)}
        placement="bottomLeft"
        align={{ offset: [0, 8] }}
        overlayInnerStyle={{ padding: 0, backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}
        arrow={false}
      >
        <Input
          ref={inputRef}
          size='large'
          className='search-input'
          prefix={<SearchIcon />}
          suffix={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {inputValue ? (
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    setInputValue('');
                    setPopoverOpen(true);
                    inputRef.current?.focus();
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px',
                    color: '#b3b3b3',
                    marginRight: 4,
                  }}
                  title='Clear search'
                >
                  <svg data-encore-id="icon" role="img" aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="currentColor">
                    <path d="M2.47 2.47a.75.75 0 0 1 1.06 0L8 6.94l4.47-4.47a.75.75 0 1 1 1.06 1.06L9.06 8l4.47 4.47a.75.75 0 1 1-1.06 1.06L8 9.06l-4.47 4.47a.75.75 0 0 1-1.06-1.06L6.94 8 2.47 3.53a.75.75 0 0 1 0-1.06Z"></path>
                  </svg>
                </button>
              ) : null}
              <span style={{ width: 1, height: 22, backgroundColor: '#535353', margin: '0 10px' }} />
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  setInputValue('');
                  navigate('/search');
                  setPopoverOpen(false);
                }}
                className={`navbar-browse-button ${isBrowse ? 'active' : ''}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                  color: isBrowse ? '#ffffff' : '#b3b3b3',
                  transition: 'color 0.15s ease',
                }}
                title='Browse'
              >
                {isBrowse ? <ActiveBrowseIcon /> : <BrowseIcon />}
              </button>
            </div>
          }
          value={inputValue}
          onKeyDown={(e) => {
            const totalItems = globalSuggestions.length;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!popoverOpen) {
                setPopoverOpen(true);
                return;
              }
              if (totalItems > 0) {
                setSelectedIndex((prev) => (prev + 1 >= totalItems ? 0 : prev + 1));
              }
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (!popoverOpen) {
                setPopoverOpen(true);
                return;
              }
              if (totalItems > 0) {
                setSelectedIndex((prev) => (prev <= 0 ? totalItems - 1 : prev - 1));
              }
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (selectedIndex >= 0 && selectedIndex < globalSuggestions.length) {
                handleSearchSubmit(globalSuggestions[selectedIndex]);
              } else {
                handleSearchSubmit();
              }
            } else if (e.key === 'Escape') {
              setPopoverOpen(false);
              setSelectedIndex(-1);
            }
          }}
          onPressEnter={(e) => {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < globalSuggestions.length) {
              handleSearchSubmit(globalSuggestions[selectedIndex]);
            } else {
              handleSearchSubmit();
            }
          }}
          onChange={(e) => {
            setInputValue(e.target.value);
            setSelectedIndex(-1);
            setPopoverOpen(true);
          }}
          onClick={() => {
            setSelectedIndex(-1);
            setPopoverOpen(true);
          }}
          onFocus={() => {
            isFocusedRef.current = true;
            setSelectedIndex(-1);
            setPopoverOpen(true);
          }}
          onBlur={() => {
            isFocusedRef.current = false;
          }}
          placeholder={t('SearchPlaceholder')}
        />
      </Popover>
    </Space>
  );
});
Search.displayName = 'Search';
export default Search;

